import { Button, Flex, Text, TextArea } from '@radix-ui/themes'
import { ComponentProps, ReactNode, useState } from 'react'
import { Modal } from './modal'

export type ReasonModalProps = {
  title: string
  /** Screen-reader description of what confirming does. */
  description: string
  confirmLabel: string
  confirmColor?: ComponentProps<typeof Button>['color']
  isPending: boolean
  errorMessage: string | null
  onCancel: () => void
  /** Called with the trimmed reason; the caller closes the modal on success. */
  onConfirm: (reason: string) => void
  /** Content shown above the reason field, such as a warning. */
  children?: ReactNode
}

/** Confirmation modal for an audited staff action that requires a reason. */
export const ReasonModal = ({
  title,
  description,
  confirmLabel,
  confirmColor,
  isPending,
  errorMessage,
  onCancel,
  onConfirm,
  children,
}: ReasonModalProps) => {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()

  return (
    <Modal
      title={title}
      hideTitle={false}
      description={description}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel()
        }
      }}>
      <Flex direction="column" gap="3" mt="3">
        {children}
        <Text size="2" color="gray">
          This is recorded in the audit trail.
        </Text>
        <TextArea
          placeholder="Reason (required)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {errorMessage && (
          <Text color="red" size="2">
            {errorMessage}
          </Text>
        )}
        <Flex gap="3" justify="end">
          <Button variant="soft" color="gray" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color={confirmColor}
            onClick={() => onConfirm(trimmed)}
            loading={isPending}
            disabled={trimmed.length === 0 || isPending}>
            {confirmLabel}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
